import { isObject } from './guards';
/**
 * @description Creates a deep copy of a value, including support for Dates, RegExp,
 * Maps, Sets, Arrays, and robust handling of circular references via a cache (WeakMap).
 * This function attempts to use `structuredClone` for performance and correctness
 * before falling back to a custom recursive implementation.
 *
 * @template T The type of the value being cloned.
 * @param {T} sourceValue The value to be cloned.
 * @param {WeakMap<object, object>} [cloningCache=new WeakMap()] **Private/Internal**: Used to track
 * already cloned objects to resolve circular references.
 * @returns {T} A deep copy of the source value.
 * @example
 * // Example with circular reference
 * const objA = { value: 1 };
 * const objB = { refA: objA };
 * objA.refB = objB; // Circular reference
 *
 * const deepCopy = deepClone(objA);
 * console.log(deepCopy.refB.refA === deepCopy); // false (It's a deep copy)
 */
export const deepClone = (sourceValue, cloningCache = new WeakMap()) => {
    // 1. Handle primitive values, null, and undefined.
    // If the value is not an object (or is null), it is a primitive and can be returned directly.
    if (!isObject(sourceValue)) {
        return sourceValue;
    }
    if (typeof structuredClone === 'function') {
        try {
            return structuredClone(sourceValue);
        }
        catch (e) {
            // If structuredClone fails (e.g., due to Functions, DOM nodes, or Symbols that are not supported),
            // the execution continues to the custom fallback implementation.
        }
    }
    // Handle circular references.
    // Checks if the source object has already been processed in the current cloning path.
    if (cloningCache.has(sourceValue)) {
        // If it exists, return the previously created clone to break the cycle.
        return cloningCache.get(sourceValue);
    }
    // Checks if the sourceValue is an instance of Date.
    if (sourceValue instanceof Date) {
        // Creates a new Date instance using the time value of the original, ensuring a distinct object.
        return new Date(sourceValue.getTime());
    }
    // Checks if the sourceValue is an instance of RegExp.
    if (sourceValue instanceof RegExp) {
        // Creates a new RegExp with the same pattern source and flags (g, i, m, u, y), creating a proper copy.
        return new RegExp(sourceValue.source, sourceValue.flags);
    }
    // Variable to hold the newly created clone object/array.
    let resultClone;
    // Checks if the sourceValue is an array.
    if (Array.isArray(sourceValue)) {
        // Initializes the clone as an empty array.
        resultClone = [];
        // Checks if the sourceValue is an instance of Map.
    }
    else if (sourceValue instanceof Map) {
        // Initializes the clone as a new empty Map.
        resultClone = new Map();
        // Checks if the sourceValue is an instance of Set.
    }
    else if (sourceValue instanceof Set) {
        // Initializes the clone as a new empty Set.
        resultClone = new Set();
        // All other objects (including class instances)
    }
    else {
        // For general objects, creates a new object instance while preserving the original object's prototype chain.
        // This is important for class instances to maintain their `instanceof` relationship.
        resultClone = Object.create(Object.getPrototypeOf(sourceValue));
    }
    // Add the clone to the cache BEFORE proceeding to recursive cloning.
    cloningCache.set(sourceValue, resultClone);
    // Map
    if (sourceValue instanceof Map) {
        for (const [key, value] of sourceValue.entries()) {
            // Recursively clones both the key and the value, and sets them in the new Map clone.
            resultClone.set(deepClone(key, cloningCache), deepClone(value, cloningCache));
        }
    }
    else if (sourceValue instanceof Set) {
        for (const value of sourceValue.values()) {
            // Recursively clones the value and adds it to the new Set clone.
            resultClone.add(deepClone(value, cloningCache));
        }
    }
    else {
        for (const key in sourceValue) {
            // Ensures that the property belongs directly to the object and is not inherited from the prototype chain.
            if (Object.prototype.hasOwnProperty.call(sourceValue, key)) {
                // Gets the current value of the property using Reflect.get for robustness.
                const value = Reflect.get(sourceValue, key);
                // Recursively clones the value and sets it on the corresponding property of the clone using Reflect.set.
                Reflect.set(resultClone, key, deepClone(value, cloningCache));
            }
        }
    }
    return resultClone;
};
/**
 * @description Recursively merges properties from one or more source objects into a target object.
 * This is a **destructive** operation: the target object is mutated (modified) directly.
 * Array properties are **overwritten**, not merged.
 *
 * lodash : `_.merge()`
 *
 * @param {IndexableObject} targetObject The destination object, which will be mutated.
 * @param {...IndexableObject} sourceObjects Source objects whose properties will be merged into the target.
 * @returns {IndexableObject} The merged target object.
 * @example
 * // Example:
 * const defaultOptions = { settings: { theme: 'dark', size: 10 }, data: [1, 2] };
 * const userOptions = { settings: { size: 20 }, version: 1 };
 *
 * deepMerge(defaultOptions, userOptions);
 * // defaultOptions is now: { settings: { theme: 'dark', size: 20 }, data: [1, 2], version: 1 }
 */
export const deepMerge = (targetObject, ...sourceObjects) => {
    if (!sourceObjects.length) {
        return targetObject;
    }
    // Takes the next source object from the beginning of the array.
    const __currentSource = sourceObjects.shift();
    if (isObject(targetObject) && isObject(__currentSource)) {
        // Iterates over all own, enumerable keys (properties) in the current source object.
        for (const key in __currentSource) {
            // Ensures the property is an own property of the source object.
            if (Object.prototype.hasOwnProperty.call(__currentSource, key)) {
                // This is the recursive step for deep merging.
                if (isObject(__currentSource[key]) && isObject(targetObject[key])) {
                    // Recursively call deepMerge on the nested objects, mutating the target's nested object.
                    deepMerge(targetObject[key], __currentSource[key]);
                    // Prevents shallow copy if the source property is an object itself.
                }
                else if (isObject(__currentSource[key])) {
                    // Creates a full deep clone of the source property and assigns it to the target property.
                    Reflect.set(targetObject, key, deepClone(__currentSource[key]));
                }
                else {
                    // Simply assigns the source value to the target property, overwriting any existing value.
                    Reflect.set(targetObject, key, __currentSource[key]);
                }
            }
        }
    }
    return deepMerge(targetObject, ...sourceObjects);
};
/**
 * @description Plytko (shallow) kopíruje všetky enumerovateľné vlastnosti (vlastné aj zdedené)
 * z jedného alebo viacerých zdrojových objektov do cieľového objektu.
 *
 * Lodash : `_.assignIn()` or `_.extend()`.
 *
 * @param {IndexableObject} targetObject Cieľový objekt (mutovaný).
 * @param {...IndexableObject} sourceObjects Zdrojové objekty.
 * @returns {IndexableObject} Mutovaný cieľový objekt.
 */
export const shallowAssign = (targetObject, ...sourceObjects) => {
    if (!sourceObjects.length) {
        return targetObject;
    }
    for (const source of sourceObjects) {
        if (source !== null && typeof source === 'object') {
            for (const key in source) {
                Reflect.set(targetObject, key, source[key]);
            }
        }
    }
    return targetObject;
};
/**
 * @description Rekurzívne zlučuje vlastnosti, vrátane tých, ktoré sú zdedené z prototypu (Inherited Properties).
 * Toto je **deštruktívna** operácia a mutuje cieľový objekt (targetObject).
 * Array properties sú prepísané, nie zlúčené.
 *
 * @param {IndexableObject} targetObject Cieľový objekt.
 * @param {...IndexableObject} sourceObjects Zdrojové objekty.
 * @returns {IndexableObject} Zlúčený cieľový objekt.
 */
export const deepAssign = (targetObject, ...sourceObjects) => {
    if (!sourceObjects.length) {
        return targetObject;
    }
    const __currentSource = sourceObjects.shift();
    if (isObject(targetObject) && isObject(__currentSource)) {
        for (const key in __currentSource) {
            if (isObject(__currentSource[key]) && isObject(targetObject[key])) {
                deepAssign(targetObject[key], __currentSource[key]);
            }
            else if (isObject(__currentSource[key])) {
                targetObject[key] = deepClone(__currentSource[key]);
            }
            else {
                targetObject[key] = __currentSource[key];
            }
        }
    }
    return deepAssign(targetObject, ...sourceObjects);
};
//# sourceMappingURL=objects.js.map